/** @format */
import isPropValid from '@emotion/is-prop-valid';
import { MpbLogo } from '@mpb-com/feather/components/atoms/mpb-logo';
import { useTranslation } from 'next-i18next';
import React, { FunctionComponent, useContext } from 'react';
import styled from 'styled-components';

import { getConfig } from '@toucan/common/services/nextjs/settings';

import { MarketCode, MarketContext } from '@toucan/client/global/market';
import { WrappedLink } from '@toucan/client/global/nextjs';
import { TRUSTPILOT_TEMPLATE_IDS } from '@toucan/client/global/trustpilot';

import {
    ConstrainedWrapper,
    ConstrainedWrapperGutter,
    ConstrainedWrapperType,
} from '@toucan/client/shared/components/constrained-wrapper';
import { CopyrightNotice } from '@toucan/client/shared/components/copyright-notice';
import {
    LinkProps,
    LinkSize,
    LinkVariant,
} from '@toucan/client/shared/components/link';
import {
    LinkList,
    LinkListVariant,
} from '@toucan/client/shared/components/link-list';
import { PaypalCreditNotice } from '@toucan/client/shared/components/paypal-credit-notice';
import { TrustedShopsBadge } from '@toucan/client/shared/components/trusted-shops-badge';
import {
    Trustpilot,
    TrustpilotTheme,
} from '@toucan/client/shared/components/trustpilot';

import { styles } from './restricted-footer.styles';

// eslint-disable-next-line @mpb-com/mpb/require-constants-file -- Legacy code: do not use as an example.
export enum RestrictedFooterVariant {
    DESKTOP = 'desktop',
    MOBILE = 'mobile',
}

// eslint-disable-next-line @mpb-com/mpb/require-types-file -- Legacy code: do not use as an example.
export type RestrictedFooterProps = {
    /**
     * An array representing the links to be displayed in footer nav.
     */
    linkList: LinkProps[];

    /**
     * The footer variant to display, one of:
     *
     * 1. DESKTOP
     * 2. MOBILE
     *
     */
    variant: RestrictedFooterVariant;

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;
};

const StyledFooter = styled('footer').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

export const RestrictedFooter: FunctionComponent<RestrictedFooterProps> = ({
    linkList,
    variant,
    ...otherProps
}: RestrictedFooterProps) => {
    const {
        publicRuntimeConfig: { trustedShopsId, marketsWithTrustedShops },
    } = getConfig();
    const { t } = useTranslation();
    const { activeMarket } = useContext(MarketContext);

    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    const displayTrustedShopsBadge = marketsWithTrustedShops?.includes(
        activeMarket.code
    );

    const trustpilotTemplateId =
        variant === RestrictedFooterVariant.DESKTOP
            ? TRUSTPILOT_TEMPLATE_IDS.microCombo
            : TRUSTPILOT_TEMPLATE_IDS.microStar;

    return (
        <StyledFooter variant={variant} {...otherProps}>
            <ConstrainedWrapper
                className="content-wrapper"
                type={ConstrainedWrapperType.PADDING}
                gutter={ConstrainedWrapperGutter.LARGE_GUTTER}
            >
                <div className="logos">
                    <WrappedLink
                        variant={LinkVariant.CUSTOM}
                        prefetch={false}
                        href="/"
                        as={`/${activeMarket.slug}`}
                        templatePath="/"
                    >
                        <MpbLogo
                            displayStrapline
                            data-testid="restricted-footer__logo"
                            className="mpb-logo"
                        />
                    </WrappedLink>
                </div>

                <div className="restricted-footer-content">
                    <nav className="links">
                        <LinkList
                            links={linkList}
                            variant={LinkListVariant.INLINE}
                            separatorSize={LinkSize.MEDIUM}
                            data-testid="restricted-footer__links"
                        />
                    </nav>
                    {activeMarket.code === MarketCode.EN_UK && (
                        <PaypalCreditNotice
                            className="credit-message"
                            data-testid="restricted-footer__credit-notice"
                        />
                    )}
                </div>

                {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
                {displayTrustedShopsBadge && (
                    <div className="trusted-shops-container">
                        <TrustedShopsBadge
                            trustedShopsId={trustedShopsId}
                            id="trustedShopsBadge"
                            className="trusted-shops-badge"
                            data-testid="restricted-footer__trusted-shops-badge"
                        />
                    </div>
                )}

                <div className="restricted-footer-trustpilot-container">
                    <Trustpilot
                        templateId={trustpilotTemplateId}
                        theme={TrustpilotTheme.LIGHT}
                        width="100%"
                        height="25px"
                        data-testid="restricted-footer__trustpilot-widget"
                    />
                </div>

                <CopyrightNotice
                    year={new Date().getFullYear()}
                    copyrightHolder={t('MPB', { context: 'copyright' })}
                    className="copyright-notice"
                    data-testid="restricted-footer__copyright-notice"
                />
            </ConstrainedWrapper>
        </StyledFooter>
    );
};
